/**
 * Created by noOne on 12/12/2017.
 */
/**
 * Best performing path finding algorithm in cases where node distances are all the same (unweighted graph / grid).
 * Could be adapted to use without a target in which case it will find path to every node, which has a slightly different use case
 * @class
 */
export default class BreadthFirst {
    constructor() {

        /**
         * Our graph or grid (the map representation)
         * @type {null}
         * @private
         */
        this._graph = null;

        /**
         * Source node (where does our agent start)
         * @type {null}
         * @private
         */
        this._source = null;

        /**
         * Target node (where is our agent going)
         * @type {null}
         * @private
         */
        this._target = null;

        /**
         * List of nodes currently being explored
         * @type {Array}
         * @private
         */
        this._openList = [];
    }

    init() {

        if (!this._graph) throw new Error('must set graph before init');
        if (!this._source) throw new Error('must set source before init');

        let startNode;

        for (let x = 0; x < this._graph.length; x++) {
            for (let y = 0; y < this._graph[x].length; y++) {

                const vert = this._graph[x][y];

                vert.neighbours = [];
                vert.prev = null;

                vert.x = x;
                vert.y = y;

                //--find the nodes neighbours (make sure to override)--//
                this.findNeighbours(vert);

                if (BreadthFirst.compareXY({x: x, y: y}, this._source)) {//if node is source node we can immediately mark as visited
                    vert.visited = true;
                    startNode = vert;
                }
            }
        }

        //--we want the list to be fifo--//
        this._openList.reverse();//why reverse an empty list? - todo remove
        this._openList.push(startNode);

    }

    /**
     * Override this function with your own function that finds neighbours appropriately and includes collision however this
     * is defined in your graph representation.
     * @abstract
     * @param vertex {{neighbours: Array, prev: Object}}
     */
    findNeighbours(vertex) {
        throw new Error("abstract functions must be overridden! : BFS.findNeighbours()");

        //minimal example function, could also include collision with something like - if (vertex.blocked) return;
        if (vertex.x > 0) vertex.neighbours.push(this._graph[vertex.x - 1][vertex.y]);//grid sq left
        if (vertex.x < this._graph.length - 1) vertex.neighbours.push(this._graph[vertex.x + 1][vertex.y]);//grid sq right
        if (vertex.y > 0) vertex.neighbours.push(this._graph[vertex.x][vertex.y - 1]);//grid sq above
        if (vertex.y < this._graph[vertex.x].length - 1) vertex.neighbours.push(this._graph[vertex.x][vertex.y + 1]);//grid sq below
    }

    findPath() {

        if (!this._target) throw new Error('must set target before performing BFS');

        const arr = [];
        let endNode;

        while (this._openList.length > 0) {

            //--get our first vertex--//
            const u = this._openList[this._openList.length - 1];

            //--visit this node (remove from open list and mark as visited)--//
            this._openList.pop();
            u.visited = true;

            //--if it is our target node we are done--//
            if (BreadthFirst.compareXY(u, this._target)) {
                console.warn("found target");
                endNode = u;
                break;
            }

            //--otherwise lets explore the neighbours--//
            for (let i = 0; i < u.neighbours.length; i++) {
                const v = u.neighbours[i];
                if (!v.visited) {
                    v.visited = true;
                    v.prev = u;

                    this._openList.unshift(v);//fifo

                    const edge = {start: u, end: v};
                    arr.push(edge);
                }
            }
        }

        const path = [];

        let node = endNode;
        //--trace back to start node to create the path--//
        var recurse = ()=> {   //using a little recursive function to trace the path backwards
            path.push({x: node.x, y: node.y});
            if (node.prev) {
                node = node.prev;
                recurse();
            }
        };
        recurse();

        return path;
    }

    /**
     * Pass in a container to run a simple test
     * @param scene {PIXI.Container}
     */
    test(scene) {

        const tWid = 25;
        const holder = new PIXI.Container();
        const gridSize = 25;
        const graph = P3.utils.matrix(gridSize, gridSize);

        holder.x = tWid * (gridSize * 0.5);
        scene.addChild(holder);

        for (let x = 0; x < gridSize; x++) {
            for (let y = 0; y < gridSize; y++) {

                let sp = new PIXI.Sprite(PIXI.Texture.WHITE);
                sp.anchor.set(0.5);
                holder.addChild(sp);
                sp.x = x * tWid;
                sp.y = y * tWid + tWid;

                sp.width = tWid - 2;
                sp.height = tWid - 2;

                sp.tint = 0xD8D8D8;

                graph[x][y] = {x: x, y: y};//grid should be stored as int and we will multiply out to get tile pos

                if (Math.random() < 0.1) {
                    //set blocked tile
                    sp.tint = 0x3C3CCC;
                    graph[x][y].blocked = true;
                }
            }
        }

        this.graph = graph;
        this.source = graph[0][0]; //set source to be top left
        this.target = {x: gridSize - 1, y: gridSize - 1};

        let t0 = performance.now();
        this.init();
        const path = this.findPath();
        let t1 = performance.now();
        console.warn("found paths in : " + (t1 - t0));

        //--create a sprite and put it in top left corner of graph--//
        let sp = new PIXI.Sprite(PIXI.Texture.WHITE);
        sp.tint = 0xff0000;
        holder.addChild(sp);
        sp.anchor.set(0.5);
        sp.position.set(graph[0][0].x, graph[0][0].y);
        sp.width = tWid * 0.5;
        sp.height = tWid * 0.5;

        followPath(sp, tWid, path);

        /**
         * Simple recursive function that tests the path by tweening the sprite along it
         * @param sprite
         * @param tWid
         * @param path
         */
        function followPath(sprite, tWid, path) {
            let node = path[path.length - 1];
            TweenMax.to(sprite, 0.2, {
                x: node.x * tWid,
                y: node.y * tWid + tWid,
                ease: Power0.easeNone,
                onComplete: ()=> {
                    path.pop();
                    if (path.length > 0) followPath(sprite, tWid, path);
                }
            })
        }
    }

    /**
     * Compares two points
     * @param point1 {PIXI.Point}
     * @param point2 {PIXI.Point}
     * @returns {boolean}
     */
    static compareXY(point1, point2) {
        return (Math.round(point1.x) === Math.round(point2.x) && Math.round(point1.y) === Math.round(point2.y));
    }

    set graph(value) {
        this._graph = value;
    }

    set source(value) {
        this._source = value;
    }

    set target(value) {
        this._target = value;
    }
}